import { confirmationToast, dismissToast, errorToast } from "@/components/ui/toast.tsx";
import { getApiOrigin } from "@/lib/utils.ts";
import { connectErrorAdminLink, connectErrorCopy } from "@/shared/connectErrorCopy.ts";
import { providerDisplayName } from "@/shared/connectors/providers.ts";
import type { McpOAuthFailure } from "@/shared/connectors/mcpOAuthErrors.ts";
import { isMcpOAuthErrorCode } from "@/types/mcp.ts";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

/**
 * Listens for OAuth results broadcast from the add-connector popup tab,
 * refreshes connector state, and shows failure toasts (success shows in
 * place).
 *
 * The connect button opens the provider in its own tab, so the tab the user
 * started from learns nothing on its own — this broadcast is the only thing that
 * tells it to refresh. A surface with a connect button and no subscription here
 * just goes stale (BIG-7231).
 *
 * `GlobalSockets` holds one app-wide subscription so the refresh survives the
 * user navigating away mid-flow, which is what a per-surface subscription could
 * not do (BIG-7329). Mount it again only on a surface that wants the toasts, or
 * that reads connector state the app-wide keys don't cover.
 *
 * `showToasts: false` keeps the refresh and drops the toast, for a surface that
 * can legitimately be on screen more than once — several connector cards in one
 * thread would otherwise each announce the same connection. Those surfaces show
 * the result in place instead, by flipping the row to "Connected".
 *
 * `onSlackConnected` hears a Slack connect that came from Settings or the setup
 * connectors card (`autoClose`), so one subscriber can offer to add okthx to
 * its channels. A connect from a chat Connect card is left out: that thread
 * gets the same offer as a card. Pass it from one subscription only, or the
 * offer opens once per subscriber; every tab hears the result, so the
 * subscriber still has to check this tab started the connect
 * (`pendingConnect.ts`).
 *
 * `onTeamsConnected` is the same for a Teams connect, whose subscriber offers
 * to add okthx to Teams.
 */
export function useConnectorOAuthResults({
  showToasts = true,
  onMcpFailure,
  onSlackConnected,
  onTeamsConnected,
}: {
  showToasts?: boolean;
  onMcpFailure?: (failure: McpOAuthFailure) => void;
  onSlackConnected?: (connected: { teamId: string }) => void;
  onTeamsConnected?: () => void;
} = {}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = new BroadcastChannel("oauth_result");

    channel.onmessage = (
      event: MessageEvent<{
        provider: string;
        displayName?: string | null;
        success: boolean;
        error?: string;
        autoClose?: boolean;
        teamId?: string;
      }>,
    ) => {
      const { provider, success, error, autoClose, teamId } = event.data;
      // Prefer the connector's own name (e.g. an OpenAPI spec's "Google Docs") over
      // the provider type; never show a raw provider id like "openapi" to the user.
      const displayName = event.data.displayName ?? providerDisplayName(provider);
      // The admin-link toast stays until closed, so any other result for the
      // provider clears it rather than leaving stale advice on screen. Sonner
      // keeps an id it just dismissed hidden, so a result that shows the toast
      // again must not dismiss it first.
      const adminLinkToastId = `connector-oauth-admin-link:${provider}`;
      const adminLink = success ? undefined : connectErrorAdminLink(error, getApiOrigin());
      if (!adminLink) dismissToast(adminLinkToastId);
      if (success) {
        queryClient.invalidateQueries({ queryKey: ["connectors"] });
        // The gallery grid's connected state comes from the catalog query, not the
        // connectors list — refresh it too so the card flips to "Connected".
        queryClient.invalidateQueries({ queryKey: ["connectorCatalog"] });
        queryClient.invalidateQueries({ queryKey: ["mcpServers"] });
        queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        // Calendar sync runs async after connector creation; the newly synced
        // calendars arrive later via the `calendar:calendars_changed` socket
        // event (see useCalendarsChangedSocket). This immediate invalidation
        // only flushes whatever calendar data is already cached.
        queryClient.invalidateQueries({ queryKey: ["calendars"] });
        if (provider === "slack" && autoClose && teamId) onSlackConnected?.({ teamId });
        if (provider === "teams" && autoClose) onTeamsConnected?.();
        // No success toast: every subscribing surface shows the result in
        // place (a tile or row flips to "Connected"), and during onboarding
        // the calendar's subscription would announce over the setup card.
        return;
      }
      if (provider === "mcp") {
        const mcpError = isMcpOAuthErrorCode(error) ? error : "mcp_authorization_failed";
        if (mcpError !== "mcp_cancelled") {
          onMcpFailure?.({
            code: mcpError,
            displayName: displayName ?? "MCP server",
          });
        }
        // MCP failures are owned by the single app-wide dialog. Treat a
        // callback from an older backend as the safe generic code, and keep
        // surface-level listeners from also showing a toast.
        return;
      }
      // A failure changes nothing to invalidate, so a refresh-only subscriber
      // has nothing left to do here.
      if (!showToasts) return;
      // Every toasting subscriber on screen hears the same broadcast. A shared
      // id makes the second one replace the first instead of stacking an
      // identical copy under it (BIG-8703).
      const toastConfig = { id: `connector-oauth-failure:${provider}` };
      const specific = connectErrorCopy(error);
      if (specific && adminLink) {
        errorToast(
          specific,
          { id: adminLinkToastId, duration: Infinity },
          {
            dismissible: true,
            actionText: "Copy admin link",
            onAction: () => copyAdminLink(adminLink, adminLinkToastId),
          },
        );
      } else if (specific) {
        errorToast(specific, toastConfig);
      } else if (error === "account_conflict") {
        errorToast("This account is already linked to another user.", toastConfig);
      } else if (error === "account_mismatch") {
        errorToast(
          "You signed in with a different account than expected. Please try again.",
          toastConfig,
        );
      } else {
        errorToast(
          displayName
            ? `Failed to connect ${displayName}. Please try again.`
            : "Failed to connect. Please try again.",
          toastConfig,
        );
      }
    };
    return () => {
      channel.close();
    };
  }, [onMcpFailure, onSlackConnected, onTeamsConnected, queryClient, showToasts]);
}

/**
 * Copies the admin approval link, or, where the page can't use the clipboard,
 * shows the link itself in a toast that stays until closed.
 */
function copyAdminLink(adminLink: string, toastId: string): void {
  const showLink = () =>
    errorToast(
      `Couldn't copy the link. Send your IT admin: ${adminLink}`,
      { id: toastId, duration: Infinity },
      { dismissible: true },
    );
  if (!navigator.clipboard?.writeText) {
    showLink();
    return;
  }
  navigator.clipboard.writeText(adminLink).then(
    () => confirmationToast("Admin approval link copied"),
    (error: unknown) => {
      Sentry.captureException(error);
      showLink();
    },
  );
}
